import React, { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { motion } from "framer-motion";
import {
  Loader2,
  ShieldCheck,
  UserPlus,
  Wallet,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth, authReady, db } from "../firebase";

const AGENT_REGISTRATION_FEE = 30;

export default function AgentLogin() {
  const navigate = useNavigate();

  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [wantsAgentAccount, setWantsAgentAccount] = useState(false);
  const [acceptAgentFee, setAcceptAgentFee] = useState(false);

  useEffect(() => {
    let active = true;

    async function restoreAgentSession() {
      await authReady;
      const existingUser = auth.currentUser;
      if (!existingUser || !active) {
        return;
      }

      const snap = await getDoc(doc(db, "users", existingUser.uid));
      const profile = snap.exists() ? snap.data() : null;
      
      if (!active) return;
      
      const userRole = String(profile?.role || "").toLowerCase();
      const isSuperAgent = ["superagent", "super-agent"].includes(userRole);
      const isAgent = userRole === "agent" && profile?.paid !== false;
      
      if (isSuperAgent) {
        navigate("/superagent/dashboard", { replace: true });
      } else if (isAgent) {
        navigate("/agent/dashboard", { replace: true });
      }
    }

    restoreAgentSession();

    return () => {
      active = false;
    };
  }, [navigate]);

  const PAYSTACK_KEY =
    import.meta.env?.VITE_PAYSTACK_KEY ||
    process.env.REACT_APP_PAYSTACK_KEY ||
    "pk_live_ed1243b2a5430c54b48c055d631c22055e1ebb43";

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setWantsAgentAccount(false);
    setAcceptAgentFee(false);
  };

  const containerClass = "min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_top,#dbe8ff_0%,#eef2ff_28%,#f5f6fa_62%,#eef1f5_100%)] p-4";

  const cardClass = "w-full max-w-lg overflow-hidden rounded-[30px] border border-[1px] border-white/70 bg-white/92 text-black shadow-[0_28px_80px_rgba(39,65,147,0.18)] backdrop-blur";

  const createUserProfile = async ({
    userId,
    cleanEmail,
    cleanPhone,
    role,
    paid,
    approved,
    onboardingReference = null,
    onboardingFee = null,
  }) => {
    const composedFullName = `${firstName.trim()} ${lastName.trim()}`.trim();

    await setDoc(doc(db, "users", userId), {
      email: cleanEmail,
      role,
      approved,
      paid,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      fullName: composedFullName,
      businessName: composedFullName,
      phone: cleanPhone,
      walletBalance: 0,
      onboardingFee,
      onboardingReference,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const handleAgentRegistrationSuccess = async (cleanEmail, cleanPassword, reference) => {
    const cleanPhone = phone.trim();
    const cred = await createUserWithEmailAndPassword(auth, cleanEmail, cleanPassword);

    await createUserProfile({
      userId: cred.user.uid,
      cleanEmail,
      cleanPhone,
      role: "agent",
      paid: true,
      approved: true,
      onboardingReference: reference,
      onboardingFee: AGENT_REGISTRATION_FEE,
    });

    await setDoc(doc(db, "walletTransactions", `${cred.user.uid}_signup_${Date.now()}`), {
      agentId: cred.user.uid,
      amount: 0,
      type: "registration",
      note: "Agent onboarding payment completed",
      reference,
      createdAt: serverTimestamp(),
    });
  };

  async function signInPortalAccount(cleanEmail, cleanPassword) {
    await authReady;
    const cred = await signInWithEmailAndPassword(auth, cleanEmail, cleanPassword);
    const snap = await getDoc(doc(db, "users", cred.user.uid));

    if (!snap.exists()) {
      await auth.signOut();
      throw new Error("Account profile not found.");
    }

    const data = snap.data();
    const userRole = String(data.role || "").toLowerCase();
    const isSuperAgent = ["superagent", "super-agent"].includes(userRole);
    const isAgent = userRole === "agent" && data.paid !== false;
    
    if (isSuperAgent) {
      navigate("/superagent/dashboard");
      return;
    }
    
    if (isAgent) {
      navigate("/agent/dashboard");
      return;
    }
    
    if (userRole === "agent" && !data.paid) {
      await auth.signOut();
      throw new Error("Registration payment not confirmed for this agent account.");
    }

    navigate("/");
  }

  async function registerStandardUser(cleanEmail, cleanPassword) {
    await authReady;

    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      throw new Error("Complete all profile fields.");
    }

    const cleanPhone = phone.trim();
    if (!/^0\d{9}$/.test(cleanPhone)) {
      throw new Error("Phone number must be 10 digits and start with 0.");
    }

    const signInMethods = await fetchSignInMethodsForEmail(auth, cleanEmail);
    if (signInMethods.length > 0) {
      throw new Error("An account with this email already exists.");
    }

    const cred = await createUserWithEmailAndPassword(auth, cleanEmail, cleanPassword);
    await createUserProfile({
      userId: cred.user.uid,
      cleanEmail,
      cleanPhone,
      role: "user",
      paid: false,
      approved: false,
    });

    navigate("/");
  }

  async function registerAgent(cleanEmail, cleanPassword) {
    await authReady;
    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      throw new Error("Complete all profile fields.");
    }

    if (!/^0\d{9}$/.test(phone.trim())) {
      throw new Error("Phone number must be 10 digits and start with 0.");
    }

    const signInMethods = await fetchSignInMethodsForEmail(auth, cleanEmail);
    if (signInMethods.length > 0) {
      throw new Error("An account with this email already exists.");
    }

    if (!window.PaystackPop) {
      throw new Error("Payment gateway is not available right now.");
    }

    await new Promise((resolve, reject) => {
      window.PaystackPop.setup({
        key: PAYSTACK_KEY,
        email: cleanEmail,
        amount: AGENT_REGISTRATION_FEE * 100,
        currency: "GHS",
        metadata: {
          custom_fields: [
            {
              display_name: "Portal",
              variable_name: "portal",
              value: "agent_registration",
            },
            {
              display_name: "Business Name",
              variable_name: "business_name",
              value: `${firstName.trim()} ${lastName.trim()}`.trim(),
            },
          ],
        },
        callback: (response) => {
          handleAgentRegistrationSuccess(cleanEmail, cleanPassword, response.reference)
            .then(() => {
              alert("Payment successful. Agent account created.");
              navigate("/agent/dashboard");
              resolve();
            })
            .catch(() => {
              reject(new Error("Payment succeeded but account setup failed. Contact admin with your payment reference."));
            });
        },
        onClose: () => reject(new Error("Payment was cancelled.")),
      }).openIframe();
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const cleanEmail = email.trim();
    const cleanPassword = password.trim();

    if (!cleanEmail || !cleanPassword) {
      setError("Please enter email and password.");
      return;
    }

    if (cleanPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      if (isRegister) {
        if (wantsAgentAccount) {
          if (!acceptAgentFee) {
            throw new Error("You must agree to pay GHS 30 to become an agent.");
          }
          await registerAgent(cleanEmail, cleanPassword);
        } else {
          await registerStandardUser(cleanEmail, cleanPassword);
        }
      } else {
        await signInPortalAccount(cleanEmail, cleanPassword);
      }
    } catch (submitError) {
      setError(submitError.message || "Unable to continue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={containerClass}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cardClass}
      >
        <div className="bg-[linear-gradient(135deg,#274193_0%,#3157c5_58%,#86a5ff_100%)] px-6 py-7 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-[1px] border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
            <Wallet size={14} /> Account Portal
          </div>
          <h1 className="mt-4 text-2xl font-black tracking-tight">Data fie</h1>
          <p className="mt-1 max-w-sm text-sm text-blue-100">
            {isRegister
              ? "Create a user account with your profile details, or choose agent onboarding and pay GHS 30."
              : "Sign in to continue. Agent accounts go to dashboard while user accounts return to home."}
          </p>
        </div>

        <div className="p-6">
          <div className="mb-5 flex items-center gap-2 rounded-2xl bg-[#eef3ff] p-1 text-sm font-semibold text-slate-600">
            <button
              type="button"
              onClick={() => {
                setIsRegister(false);
                setError("");
              }}
              className={`flex-1 rounded-2xl px-4 py-2.5 transition ${
                !isRegister ? "bg-white text-[#274193] shadow-sm" : "text-slate-500"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setIsRegister(true);
                setError("");
                resetForm();
              }}
              className={`flex-1 rounded-2xl px-4 py-2.5 transition ${
                isRegister ? "bg-white text-[#274193] shadow-sm" : "text-slate-500"
              }`}
            >
              Sign Up
            </button>
          </div>

          {isRegister && (
            <div className="mb-5 rounded-2xl border border-[1px] border-emerald-200 bg-emerald-50 p-4 text-emerald-700">
              <p className="text-sm font-semibold">Create profile account (free), or become an agent for GHS {AGENT_REGISTRATION_FEE.toFixed(2)}.</p>
              <p className="mt-1 text-xs leading-relaxed">When agent option is selected, payment opens before account setup is completed.</p>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-2xl border border-[1px] border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 text-slate-900">
            {isRegister && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">First Name</label>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      className="w-full rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">Last Name</label>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      className="w-full rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone Number</label>
                  <div className="flex items-center overflow-hidden rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] focus-within:border-[#3157c5] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#3157c5]/10">
                    <span className="px-3 text-sm font-semibold text-slate-700">+233</span>
                    <div className="h-8 w-px bg-slate-200" />
                    <input
                      type="tel"
                      placeholder="0XXXXXXXXX"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      className="w-full bg-transparent px-3 py-3 text-sm outline-none"
                      maxLength={10}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] p-3">
                  <label className="flex items-start gap-2.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={wantsAgentAccount}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setWantsAgentAccount(checked);
                        if (!checked) {
                          setAcceptAgentFee(false);
                        }
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300"
                    />
                    <span>
                      <span className="font-semibold text-slate-800">I want to become an agent</span>
                      <span className="mt-1 block text-xs text-slate-500">Tick this only if you want agent dashboard access and onboarding payment.</span>
                    </span>
                  </label>

                  {wantsAgentAccount && (
                    <label className="mt-3 flex items-start gap-2.5 rounded-xl border border-[1px] border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800">
                      <input
                        type="checkbox"
                        checked={acceptAgentFee}
                        onChange={(event) => setAcceptAgentFee(event.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-amber-300"
                      />
                      <span>
                        <span className="font-semibold">I agree to pay GHS {AGENT_REGISTRATION_FEE.toFixed(2)} onboarding fee.</span>
                        <span className="mt-1 block text-xs">Payment is required before the agent account is created.</span>
                      </span>
                    </label>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email Address</label>
              <input
                type="email"
                placeholder="Agent email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Password</label>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-2xl border border-[1px] border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
              />
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#274193_0%,#3157c5_100%)] px-4 py-3 text-sm font-bold text-white shadow-[0_14px_28px_rgba(49,87,197,0.28)] transition hover:brightness-105 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Please wait...
                </>
              ) : isRegister ? (
                <>
                  {wantsAgentAccount ? <ShieldCheck size={16} /> : <UserPlus size={16} />}
                  {wantsAgentAccount
                    ? `Create Agent Account (Pay GHS ${AGENT_REGISTRATION_FEE})`
                    : "Create Profile Account"}
                </>
              ) : (
                "Sign In"
              )}
            </motion.button>
          </form>

          {isRegister ? (
            <div className="mt-5 text-center text-xs text-slate-500">
              <p>
                By signing up you agree to our <span className="font-bold underline">Terms of Service</span>
              </p>
            </div>
          ) : (
            <p className="mt-5 text-center text-xs leading-relaxed text-slate-500">
              User accounts can purchase directly. Agent accounts use dashboard tools after onboarding.
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
